/**
 * Surmantra Elite - Global JavaScript
 * Premium interactions and utilities
 */

(function() {
  'use strict';

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const Elite = {
    // Debounce function
    debounce(fn, delay = 250) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    // Throttle function
    throttle(fn, limit = 250) {
      let inThrottle;
      return (...args) => {
        if (!inThrottle) {
          fn.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    // Format currency
    formatMoney(cents, format = '₹{{amount}}') {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const value = (parseInt(cents) / 100).toLocaleString('en-IN');
      return format.replace('{{amount}}', value);
    },

    // Get cookie
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    },

    // Set cookie
    setCookie(name, value, days = 30) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
    },

    // Smooth scroll to element
    scrollTo(target, offset = 0) {
      const element = typeof target === 'string' ? document.querySelector(target) : target;
      if (!element) return;

      const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    },

    // Check if element is in viewport
    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      return (
        rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
        rect.bottom > 0 &&
        rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
        rect.right > 0
      );
    }
  };

  // Make Elite globally available
  window.Elite = Elite;

  // ============================================
  // SCROLL ANIMATIONS
  // ============================================

  class ScrollAnimations {
    constructor() {
      this.elements = document.querySelectorAll('[data-animate]');
      if (!this.elements.length) return;

      this.observer = new IntersectionObserver(
        this.handleIntersect.bind(this),
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      this.elements.forEach(el => this.observer.observe(el));
    }

    handleIntersect(entries) {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          // Stagger animations
          setTimeout(() => {
            entry.target.classList.add('is-visible');
          }, index * 100);
          this.observer.unobserve(entry.target);
        }
      });
    }
  }

  // ============================================
  // HEADER
  // ============================================

  class Header {
    constructor() {
      this.header = document.querySelector('.elite-header');
      if (!this.header) return;

      this.lastScroll = 0;
      this.scrollThreshold = 50;

      this.bindEvents();
    }

    bindEvents() {
      window.addEventListener('scroll', Elite.throttle(this.handleScroll.bind(this), 100));
    }

    handleScroll() {
      const currentScroll = window.pageYOffset;

      // Add scrolled class
      if (currentScroll > this.scrollThreshold) {
        this.header.classList.add('is-scrolled');
      } else {
        this.header.classList.remove('is-scrolled');
      }

      // Hide/show on scroll direction
      if (currentScroll > this.lastScroll && currentScroll > 300) {
        this.header.classList.add('is-hidden');
      } else {
        this.header.classList.remove('is-hidden');
      }

      this.lastScroll = currentScroll;
    }
  }

  // ============================================
  // MOBILE NAVIGATION
  // ============================================

  class MobileNav {
    constructor() {
      // Support both data attributes for toggle button
      this.toggle = document.querySelector('[data-mobile-nav-toggle]') || document.querySelector('[data-mobile-menu-toggle]');
      this.nav = document.querySelector('[data-mobile-nav]');
      this.overlay = document.querySelector('[data-mobile-nav-overlay]');
      this.closeBtn = document.querySelector('[data-mobile-nav-close]');
      // Links that should close the menu when clicked
      this.closeLinks = document.querySelectorAll('[data-mobile-nav-close]');

      if (!this.toggle || !this.nav) return;

      this.isOpen = false;
      this.bindEvents();
    }

    bindEvents() {
      this.toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });

      // Close button
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

      // Overlay click
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      // Links that should close menu (anchor links)
      this.closeLinks.forEach(link => {
        if (link !== this.closeBtn) {
          link.addEventListener('click', () => this.close());
        }
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });
    }

    open() {
      this.isOpen = true;
      this.nav.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      this.toggle.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.isOpen = false;
      this.nav.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
      this.toggle.setAttribute('aria-expanded', 'false');
    }
  }

  // ============================================
  // CART DRAWER
  // ============================================

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.overlay = document.querySelector('[data-cart-drawer-overlay]');
      this.closeBtn = document.querySelector('[data-cart-drawer-close]');
      // Support multiple trigger attributes
      this.triggers = document.querySelectorAll('[data-cart-trigger], [data-cart-toggle]');
      this.countElements = document.querySelectorAll('[data-cart-count]');

      if (!this.drawer) return;

      this.isOpen = false;
      this.bindEvents();
    }

    bindEvents() {
      this.triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });
    }

    open() {
      this.isOpen = true;
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    updateCount(count) {
      this.countElements.forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    }

    async refresh() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.updateCount(cart.item_count);
        return cart;
      } catch (error) {
        console.error('Cart refresh failed:', error);
      }
    }
  }

  // ============================================
  // COUNTDOWN TIMER
  // ============================================

  class CountdownTimer {
    constructor(element) {
      this.element = element;
      this.endDate = new Date(element.dataset.countdown);

      if (isNaN(this.endDate.getTime())) return;

      this.daysEl = element.querySelector('[data-countdown-days]');
      this.hoursEl = element.querySelector('[data-countdown-hours]');
      this.minutesEl = element.querySelector('[data-countdown-minutes]');
      this.secondsEl = element.querySelector('[data-countdown-seconds]');

      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    update() {
      const now = new Date().getTime();
      const distance = this.endDate.getTime() - now;

      if (distance < 0) {
        clearInterval(this.interval);
        this.element.classList.add('is-expired');
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (this.daysEl) this.daysEl.textContent = String(days).padStart(2, '0');
      if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
      if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
      if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
    }
  }

  // ============================================
  // COUNTER ANIMATION
  // ============================================

  class CounterAnimation {
    constructor(element) {
      this.element = element;
      this.target = parseInt(element.dataset.counter.replace(/,/g, ''));
      this.duration = parseInt(element.dataset.duration) || 2000;
      this.suffix = element.dataset.suffix || '';

      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.animate();
          this.observer.disconnect();
        }
      });

      this.observer.observe(element);
    }

    animate() {
      const start = 0;
      const startTime = performance.now();

      const step = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / this.duration, 1);

        // Ease out cubic
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        const current = Math.floor(start + (this.target - start) * easeProgress);

        this.element.textContent = current.toLocaleString('en-IN') + this.suffix;

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    }
  }

  // ============================================
  // ACCORDION
  // ============================================

  class Accordion {
    constructor(container) {
      this.container = container;
      this.items = container.querySelectorAll('[data-accordion-item]');
      this.allowMultiple = container.dataset.accordionMultiple === 'true';

      this.items.forEach(item => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        if (trigger) {
          trigger.addEventListener('click', () => this.toggle(item));
        }
      });
    }

    toggle(item) {
      const isOpen = item.classList.contains('is-open');

      if (!this.allowMultiple) {
        this.items.forEach(i => {
          i.classList.remove('is-open');
          const trigger = i.querySelector('[data-accordion-trigger]');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        });
      }

      if (!isOpen) {
        item.classList.add('is-open');
        const trigger = item.querySelector('[data-accordion-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      }
    }
  }

  // ============================================
  // TABS
  // ============================================

  class Tabs {
    constructor(container) {
      this.container = container;
      this.tabs = container.querySelectorAll('[data-tab]');
      this.panels = container.querySelectorAll('[data-tab-panel]');

      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.activate(tab.dataset.tab));
      });
    }

    activate(id) {
      this.tabs.forEach(tab => {
        tab.classList.toggle('is-active', tab.dataset.tab === id);
        tab.setAttribute('aria-selected', tab.dataset.tab === id);
      });

      this.panels.forEach(panel => {
        panel.classList.toggle('is-active', panel.dataset.tabPanel === id);
      });
    }
  }

  // ============================================
  // LAZY LOADING
  // ============================================

  class LazyLoad {
    constructor() {
      this.images = document.querySelectorAll('[data-lazy]');
      if (!this.images.length) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
          rootMargin: '50px 0px'
        });
        this.images.forEach(img => this.observer.observe(img));
      } else {
        // Fallback for older browsers
        this.images.forEach(img => this.load(img));
      }
    }

    handleIntersect(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.load(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }

    load(img) {
      const src = img.dataset.lazy;
      if (!src) return;

      img.src = src;
      img.removeAttribute('data-lazy');
      img.classList.add('is-loaded');
    }
  }

  // ============================================
  // SMOOTH SCROLL LINKS
  // ============================================

  class SmoothScroll {
    constructor() {
      document.querySelectorAll('a[href^="#"]').forEach(link => {
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href');
          if (href === '#') return;

          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            const headerHeight = document.querySelector('.elite-header')?.offsetHeight || 0;
            Elite.scrollTo(target, headerHeight + 20);
          }
        });
      });
    }
  }

  // ============================================
  // NOTIFICATION TOAST
  // ============================================

  class Toast {
    constructor() {
      this.container = document.createElement('div');
      this.container.className = 'toast-container';
      this.container.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 10px;
      `;
      document.body.appendChild(this.container);
    }

    show(message, type = 'info', duration = 4000) {
      const toast = document.createElement('div');
      toast.className = `toast toast--${type}`;
      toast.style.cssText = `
        padding: 16px 24px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.15);
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 12px;
        transform: translateX(120%);
        transition: transform 0.3s ease;
        max-width: 350px;
      `;

      const colors = {
        success: '#22C55E',
        error: '#EF4444',
        warning: '#EAB308',
        info: '#3B82F6'
      };

      toast.innerHTML = `
        <span style="width: 8px; height: 8px; border-radius: 50%; background: ${colors[type] || colors.info}"></span>
        <span>${message}</span>
      `;

      this.container.appendChild(toast);

      // Animate in
      requestAnimationFrame(() => {
        toast.style.transform = 'translateX(0)';
      });

      // Auto dismiss
      setTimeout(() => {
        toast.style.transform = 'translateX(120%)';
        setTimeout(() => toast.remove(), 300);
      }, duration);
    }
  }

  // Make Toast globally available
  window.toast = new Toast();

  // ============================================
  // INITIALIZATION
  // ============================================

  function init() {
    // Initialize all components
    new ScrollAnimations();
    new Header();
    new MobileNav();
    window.cartDrawer = new CartDrawer();
    new LazyLoad();
    new SmoothScroll();

    // Initialize countdown timers
    document.querySelectorAll('[data-countdown]').forEach(el => new CountdownTimer(el));

    // Initialize counter animations
    document.querySelectorAll('[data-counter]').forEach(el => new CounterAnimation(el));

    // Initialize accordions
    document.querySelectorAll('[data-accordion]').forEach(el => new Accordion(el));

    // Initialize tabs
    document.querySelectorAll('[data-tabs]').forEach(el => new Tabs(el));

    // Remove loading screen
    setTimeout(() => {
      const loading = document.querySelector('.loading-screen');
      if (loading) {
        loading.classList.add('is-hidden');
        setTimeout(() => loading.remove(), 500);
      }
    }, 500);

    console.log('🎵 Surmantra Elite initialized');
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
